import type { DraftBrandTokens, InternalUiSpec } from '@erp/json-renderer';

/**
 * Canned data for the demo customer portal.
 *
 * This stands in for what a real deployment would produce: a generated
 * json-render portal spec plus the customer's published Brand Kit. Here both
 * are hardcoded for the fictional company "Pillar" so the portal renders with
 * zero backend. Swap these for fetched values to wire up the real pipeline.
 *
 * The portal is interactive: several buttons bind `on.press` to actions
 * (`markPaid`, `resendInvoice`, `reorder`). The dashboard page supplies matching
 * handlers backed by a controlled state store, so pressing a button mutates the
 * page live (status badges, KPI totals, the activity feed). See `index.tsx`.
 */

/** The company whose brand + portal this demo renders. */
export const cannedCompanyName = 'Pillar';

/**
 * Pillar's brand kit — a Greek-with-a-modern-twist palette: sage greens, sky
 * blues, soft whites, and earthy olives/tans. Authored in the brand-kit token
 * shape (`DraftBrandTokens`) and themed onto `@erp/ui` by `PortalBrandTheme`.
 */
export const cannedBrandTokens: DraftBrandTokens = {
  version: 1,
  colors: [
    { role: 'primary', value: '#7B9A76', label: 'Sage' },
    { role: 'onPrimary', value: '#FBFAF4', label: 'Soft white' },
    { role: 'secondary', value: '#CFE3EC', label: 'Sky' },
    { role: 'onSecondary', value: '#2F3A2C', label: 'Olive ink' },
    { role: 'accent', value: '#E5EEF2', label: 'Mist' },
    { role: 'background', value: '#F6F3EC', label: 'Linen' },
    { role: 'surface', value: '#FFFFFF', label: 'White' },
    { role: 'textPrimary', value: '#2F3A2C', label: 'Olive ink' },
    { role: 'textSecondary', value: '#6C7461', label: 'Muted olive' },
    { role: 'border', value: '#D9CEB9', label: 'Tan' },
    { role: 'danger', value: '#B65C3D', label: 'Terracotta' },
    { role: 'success', value: '#5E7B4F', label: 'Olive green' },
  ],
  typography: [
    {
      role: 'heading',
      family: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
      sizePx: 28,
      weight: 600,
      lineHeight: 1.15,
    },
    {
      role: 'body',
      family: "'Inter', system-ui, -apple-system, sans-serif",
      sizePx: 14,
      weight: 400,
      lineHeight: 1.5,
    },
  ],
  voice: {
    tone: 'Calm, considered, and quietly confident.',
    doList: ['Speak plainly', 'Lead with the customer outcome'],
    dontList: ['Hype', 'Jargon'],
    taglines: ['Built to last.'],
  },
};

/** Brand colors reused inside chart marks (Vega renders to canvas, not CSS vars). */
const SAGE = '#7B9A76';
const SKY = '#3E7FA0';
/** Categorical range drawn from the Pillar palette, for the breakdown chart. */
const CATEGORY_RANGE = ['#7B9A76', '#3E7FA0', '#5E7B4F', '#C9B68C', '#B65C3D'];

// --- Year-to-date trend data -------------------------------------------------
// Charts run Jan → the current month so they never show future months. The
// month count is derived from "now", so the dashboard stays sensible over time.
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
/** Months elapsed this year, inclusive of the current one (Jan = 1). */
const MONTHS_YTD = new Date().getMonth() + 1;
/** Full-year base series, sliced to the current month below. */
const SPEND_SERIES = [
  11200, 9800, 13400, 12100, 16200, 21500, 18900, 17600, 19800, 22400, 20100,
  24700,
];
const ORDERS_SERIES = [5, 7, 6, 8, 9, 12, 10, 11, 9, 12, 11, 12];

const spendByMonth = MONTH_LABELS.slice(0, MONTHS_YTD).map((month, i) => ({
  month,
  spend: SPEND_SERIES[i],
}));
const ordersByMonth = MONTH_LABELS.slice(0, MONTHS_YTD).map((month, i) => ({
  month,
  orders: ORDERS_SERIES[i],
}));

/** Build the three elements of one interactive invoice row, keyed by index. */
function invoiceRowElements(
  index: number,
  invoice: string,
  amount: string,
): InternalUiSpec['elements'] {
  return {
    [`invoiceRow${index}`]: {
      type: 'Box',
      props: {
        direction: 'row',
        justify: 'between',
        align: 'center',
        gap: 'md',
      },
      children: [
        `invoiceInfo${index}`,
        `invoiceStatus${index}`,
        `invoiceActions${index}`,
      ],
    },
    [`invoiceInfo${index}`]: {
      type: 'Box',
      props: { direction: 'col', gap: 'none' },
      children: [`invoiceNum${index}`, `invoiceAmt${index}`],
    },
    [`invoiceNum${index}`]: {
      type: 'Text',
      props: { value: invoice, variant: 'label' },
      children: [],
    },
    [`invoiceAmt${index}`]: {
      type: 'Text',
      props: { value: amount, variant: 'muted' },
      children: [],
    },
    [`invoiceStatus${index}`]: {
      type: 'Badge',
      props: {
        label: { $state: `/invoices/${index}/status` },
        variant: { $state: `/invoices/${index}/statusVariant` },
      },
      children: [],
    },
    [`invoiceActions${index}`]: {
      type: 'Box',
      props: { direction: 'row', gap: 'sm', align: 'center' },
      children: [
        `viewBtn${index}`,
        `paidText${index}`,
        `markBtn${index}`,
        `resendBtn${index}`,
      ],
    },
    // Opens the invoice detail slide-in (a Sheet element) for this row.
    [`viewBtn${index}`]: {
      type: 'Button',
      props: { label: 'View', size: 'sm', variant: 'ghost' },
      on: { press: { action: 'openInvoice', params: { invoice } } },
      children: [],
    },
    // Shown once the invoice is paid; replaces the "Mark paid" button.
    [`paidText${index}`]: {
      type: 'Text',
      props: { value: 'Paid', variant: 'label', tone: 'success' },
      visible: { $state: `/invoices/${index}/paid` },
      children: [],
    },
    // Hidden once paid (the "Paid" text above takes its place).
    [`markBtn${index}`]: {
      type: 'Button',
      props: { label: 'Mark paid', size: 'sm', variant: 'secondary' },
      visible: { $state: `/invoices/${index}/paid`, not: true },
      on: { press: { action: 'markPaid', params: { invoice } } },
      children: [],
    },
    // Label + disabled are state-bound: "Resend" → "Sent" (disabled) after use.
    [`resendBtn${index}`]: {
      type: 'Button',
      props: {
        label: { $state: `/invoices/${index}/resendLabel` },
        size: 'sm',
        variant: 'outline',
        disabled: { $state: `/invoices/${index}/resent` },
      },
      on: { press: { action: 'resendInvoice', params: { invoice } } },
      children: [],
    },
  };
}

/**
 * The canned portal — a branded, interactive customer dashboard described by a
 * single json-render spec: welcome + announcement, a KPI grid (some values
 * bound to live state), a full year of charts, a spend breakdown, an
 * actionable open-invoices list, recent orders with a reorder action, an
 * activity feed, and support tickets. Rendered by `InternalUiRenderer`.
 */
export const cannedPortalSpec: InternalUiSpec = {
  root: 'page',
  elements: {
    page: {
      type: 'Stack',
      props: { gap: 'lg', padding: 'lg' },
      children: [
        'welcome',
        'announcement',
        'kpis',
        'charts',
        'categoryCard',
        'invoicesCard',
        'ordersCard',
        'activityCard',
        'ticketsCard',
        // Overlay — renders nothing until an invoice row's "View" opens it.
        'invoiceSheet',
      ],
    },

    welcome: {
      type: 'Card',
      props: {
        title: 'Welcome back, Noam',
        description: 'Your Pillar account at a glance.',
      },
      children: ['welcomeText'],
    },
    welcomeText: {
      type: 'Text',
      props: {
        value:
          'Everything below is a single generated portal, rendered live in your brand. Try the buttons — they update the page.',
        variant: 'muted',
      },
      children: [],
    },

    announcement: {
      type: 'Alert',
      props: {
        title: 'New: self-serve reorders',
        description:
          'You can now reorder past shipments in one click. Volume pricing applies automatically at checkout.',
      },
      children: [],
    },

    kpis: {
      type: 'Grid',
      props: { columns: 4, gap: 'md' },
      children: ['kpiSpend', 'kpiOrders', 'kpiOnTime', 'kpiInvoices'],
    },
    kpiSpend: {
      type: 'Card',
      props: {},
      children: ['kpiSpendLabel', 'kpiSpendValue', 'kpiSpendTrend'],
    },
    kpiSpendLabel: {
      type: 'Text',
      props: { value: 'Spend YTD', variant: 'eyebrow' },
      children: [],
    },
    kpiSpendValue: {
      type: 'Heading',
      props: { value: '$184,200', level: 2 },
      children: [],
    },
    kpiSpendTrend: {
      type: 'Text',
      props: { value: '+18% vs last year', variant: 'muted', tone: 'success' },
      children: [],
    },
    kpiOrders: {
      type: 'Card',
      props: {},
      children: ['kpiOrdersLabel', 'kpiOrdersValue', 'kpiOrdersTrend'],
    },
    kpiOrdersLabel: {
      type: 'Text',
      props: { value: 'Active orders', variant: 'eyebrow' },
      children: [],
    },
    kpiOrdersValue: {
      type: 'Heading',
      props: { value: { $state: '/kpis/activeOrders' }, level: 2 },
      children: [],
    },
    kpiOrdersTrend: {
      type: 'Text',
      props: { value: '3 shipping this week', variant: 'muted' },
      children: [],
    },
    kpiOnTime: {
      type: 'Card',
      props: {},
      children: ['kpiOnTimeLabel', 'kpiOnTimeValue', 'kpiOnTimeBadge'],
    },
    kpiOnTimeLabel: {
      type: 'Text',
      props: { value: 'On-time delivery', variant: 'eyebrow' },
      children: [],
    },
    kpiOnTimeValue: {
      type: 'Heading',
      props: { value: '98%', level: 2 },
      children: [],
    },
    kpiOnTimeBadge: {
      type: 'Badge',
      props: { label: 'On track', variant: 'success' },
      children: [],
    },
    kpiInvoices: {
      type: 'Card',
      props: {},
      children: ['kpiInvoicesLabel', 'kpiInvoicesValue', 'kpiInvoicesBadge'],
    },
    kpiInvoicesLabel: {
      type: 'Text',
      props: { value: 'Open invoices', variant: 'eyebrow' },
      children: [],
    },
    kpiInvoicesValue: {
      type: 'Heading',
      props: { value: { $state: '/kpis/openInvoicesTotal' }, level: 2 },
      children: [],
    },
    kpiInvoicesBadge: {
      type: 'Badge',
      props: {
        label: { $state: '/kpis/openInvoicesBadge' },
        variant: { $state: '/kpis/openInvoicesBadgeVariant' },
      },
      children: [],
    },

    charts: {
      type: 'Grid',
      props: { columns: 2, gap: 'md' },
      children: ['spendCard', 'usageCard'],
    },
    spendCard: {
      type: 'Card',
      props: { title: 'Spend by month' },
      children: ['spendChart'],
    },
    spendChart: {
      type: 'Chart',
      props: {
        height: 240,
        spec: {
          mark: { type: 'bar', cornerRadiusEnd: 3, color: SAGE },
          data: { values: spendByMonth },
          encoding: {
            x: { field: 'month', type: 'nominal', title: null },
            y: { field: 'spend', type: 'quantitative', title: 'Spend ($)' },
          },
        },
      },
      children: [],
    },
    usageCard: {
      type: 'Card',
      props: { title: 'Active orders' },
      children: ['usageChart'],
    },
    usageChart: {
      type: 'Chart',
      props: {
        height: 240,
        spec: {
          mark: {
            type: 'line',
            strokeWidth: 3,
            color: SKY,
            point: { filled: true, color: SKY },
          },
          data: { values: ordersByMonth },
          encoding: {
            x: { field: 'month', type: 'nominal', title: null },
            y: {
              field: 'orders',
              type: 'quantitative',
              title: 'Active orders',
            },
          },
        },
      },
      children: [],
    },

    categoryCard: {
      type: 'Card',
      props: { title: 'Spend by category' },
      children: ['categoryChart'],
    },
    categoryChart: {
      type: 'Chart',
      props: {
        height: 220,
        spec: {
          mark: { type: 'bar', cornerRadiusEnd: 3 },
          data: {
            values: [
              { category: 'Produce', amount: 68200 },
              { category: 'Packaging', amount: 41300 },
              { category: 'Logistics', amount: 33700 },
              { category: 'Equipment', amount: 24900 },
              { category: 'Services', amount: 16100 },
            ],
          },
          encoding: {
            y: {
              field: 'category',
              type: 'nominal',
              title: null,
              sort: '-x',
            },
            x: { field: 'amount', type: 'quantitative', title: 'Spend ($)' },
            color: {
              field: 'category',
              type: 'nominal',
              legend: null,
              scale: { range: CATEGORY_RANGE },
            },
          },
        },
      },
      children: [],
    },

    invoicesCard: {
      type: 'Card',
      props: {
        title: 'Open invoices',
        description: 'Pay now or request a resend — the page updates live.',
      },
      children: ['invoiceList'],
    },
    invoiceList: {
      type: 'Stack',
      props: { gap: 'md' },
      children: [
        'invoiceRow0',
        'invoiceSep0',
        'invoiceRow1',
        'invoiceSep1',
        'invoiceRow2',
      ],
    },
    invoiceSep0: { type: 'Separator', props: {}, children: [] },
    invoiceSep1: { type: 'Separator', props: {}, children: [] },
    ...invoiceRowElements(0, 'INV-8841', '$4,200.00'),
    ...invoiceRowElements(1, 'INV-8840', '$12,540.00'),
    ...invoiceRowElements(2, 'INV-8838', '$3,025.00'),

    // Invoice detail slide-in. A single reusable Sheet bound to `/invoiceSheet`;
    // a row's "View" press fills that state and flips `open` to true (see the
    // `openInvoice` handler in index.tsx). Closing writes `open` back to false.
    invoiceSheet: {
      type: 'Sheet',
      props: {
        title: { $state: '/invoiceSheet/title' },
        description: 'Invoice details',
        open: { $bindState: '/invoiceSheet/open' },
        side: 'right',
        size: 40,
      },
      children: ['invoiceSheetBody'],
    },
    invoiceSheetBody: {
      type: 'Stack',
      props: { gap: 'md' },
      children: [
        'invoiceSheetHero',
        'invoiceSheetOverdue',
        'invoiceSheetMetaGrid',
        'invoiceSheetBillTo',
        'invoiceSheetLinesCard',
      ],
    },
    // Hero: big amount + status, with the due line beneath.
    invoiceSheetHero: {
      type: 'Card',
      props: {},
      children: ['invoiceSheetHeadRow', 'invoiceSheetDue'],
    },
    invoiceSheetHeadRow: {
      type: 'Box',
      props: {
        direction: 'row',
        justify: 'between',
        align: 'center',
        gap: 'md',
      },
      children: ['invoiceSheetAmount', 'invoiceSheetStatus'],
    },
    invoiceSheetAmount: {
      type: 'Heading',
      props: { value: { $state: '/invoiceSheet/amount' }, level: 1 },
      children: [],
    },
    invoiceSheetStatus: {
      type: 'Badge',
      props: {
        label: { $state: '/invoiceSheet/status' },
        variant: { $state: '/invoiceSheet/statusVariant' },
      },
      children: [],
    },
    invoiceSheetDue: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/dueLabel' }, variant: 'muted' },
      children: [],
    },
    invoiceSheetOverdue: {
      type: 'Alert',
      props: {
        variant: 'destructive',
        title: 'Payment overdue',
        description: { $state: '/invoiceSheet/overdueNote' },
      },
      visible: { $state: '/invoiceSheet/overdue' },
      children: [],
    },
    // Metadata as compact stat cards.
    invoiceSheetMetaGrid: {
      type: 'Grid',
      props: { columns: 2, gap: 'md' },
      children: [
        'invoiceSheetPo',
        'invoiceSheetIssued',
        'invoiceSheetTerms',
        'invoiceSheetPayment',
      ],
    },
    invoiceSheetPo: {
      type: 'Card',
      props: {},
      children: ['invoiceSheetPoLabel', 'invoiceSheetPoValue'],
    },
    invoiceSheetPoLabel: {
      type: 'Text',
      props: { value: 'PO number', variant: 'eyebrow' },
      children: [],
    },
    invoiceSheetPoValue: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/po' }, variant: 'label' },
      children: [],
    },
    invoiceSheetIssued: {
      type: 'Card',
      props: {},
      children: ['invoiceSheetIssuedLabel', 'invoiceSheetIssuedValue'],
    },
    invoiceSheetIssuedLabel: {
      type: 'Text',
      props: { value: 'Issued', variant: 'eyebrow' },
      children: [],
    },
    invoiceSheetIssuedValue: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/issueDate' }, variant: 'label' },
      children: [],
    },
    invoiceSheetTerms: {
      type: 'Card',
      props: {},
      children: ['invoiceSheetTermsLabel', 'invoiceSheetTermsValue'],
    },
    invoiceSheetTermsLabel: {
      type: 'Text',
      props: { value: 'Terms', variant: 'eyebrow' },
      children: [],
    },
    invoiceSheetTermsValue: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/terms' }, variant: 'label' },
      children: [],
    },
    invoiceSheetPayment: {
      type: 'Card',
      props: {},
      children: ['invoiceSheetPaymentLabel', 'invoiceSheetPaymentValue'],
    },
    invoiceSheetPaymentLabel: {
      type: 'Text',
      props: { value: 'Payment method', variant: 'eyebrow' },
      children: [],
    },
    invoiceSheetPaymentValue: {
      type: 'Text',
      props: {
        value: { $state: '/invoiceSheet/paymentMethod' },
        variant: 'label',
      },
      children: [],
    },
    invoiceSheetBillTo: {
      type: 'Card',
      props: { title: 'Bill to' },
      children: ['invoiceSheetBillToName', 'invoiceSheetBillToAddr'],
    },
    invoiceSheetBillToName: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/billTo' }, variant: 'label' },
      children: [],
    },
    invoiceSheetBillToAddr: {
      type: 'Text',
      props: {
        value: { $state: '/invoiceSheet/billToAddress' },
        variant: 'muted',
      },
      children: [],
    },
    // Line items + totals.
    invoiceSheetLinesCard: {
      type: 'Card',
      props: { title: 'Line items' },
      children: ['invoiceSheetLines', 'invoiceSheetTotals'],
    },
    invoiceSheetLines: {
      type: 'Table',
      props: {
        columns: [
          { key: 'item', label: 'Item' },
          { key: 'qty', label: 'Qty', align: 'right' },
          { key: 'amount', label: 'Amount', align: 'right' },
        ],
        rows: { $state: '/invoiceSheet/lineItems' },
      },
      children: [],
    },
    invoiceSheetTotals: {
      type: 'Stack',
      props: { gap: 'sm' },
      children: [
        'invoiceSheetSubtotal',
        'invoiceSheetTax',
        'invoiceSheetTotalSep',
        'invoiceSheetTotal',
      ],
    },
    invoiceSheetSubtotal: {
      type: 'Box',
      props: { direction: 'row', justify: 'between' },
      children: ['invoiceSheetSubtotalLabel', 'invoiceSheetSubtotalValue'],
    },
    invoiceSheetSubtotalLabel: {
      type: 'Text',
      props: { value: 'Subtotal', variant: 'muted' },
      children: [],
    },
    invoiceSheetSubtotalValue: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/subtotal' }, variant: 'body' },
      children: [],
    },
    invoiceSheetTax: {
      type: 'Box',
      props: { direction: 'row', justify: 'between' },
      children: ['invoiceSheetTaxLabel', 'invoiceSheetTaxValue'],
    },
    invoiceSheetTaxLabel: {
      type: 'Text',
      props: { value: 'Tax', variant: 'muted' },
      children: [],
    },
    invoiceSheetTaxValue: {
      type: 'Text',
      props: { value: { $state: '/invoiceSheet/tax' }, variant: 'body' },
      children: [],
    },
    invoiceSheetTotalSep: { type: 'Separator', props: {}, children: [] },
    invoiceSheetTotal: {
      type: 'Box',
      props: { direction: 'row', justify: 'between', align: 'center' },
      children: ['invoiceSheetTotalLabel', 'invoiceSheetTotalValue'],
    },
    invoiceSheetTotalLabel: {
      type: 'Text',
      props: { value: 'Total due', variant: 'label' },
      children: [],
    },
    invoiceSheetTotalValue: {
      type: 'Heading',
      props: { value: { $state: '/invoiceSheet/total' }, level: 4 },
      children: [],
    },

    ordersCard: {
      type: 'Card',
      props: { title: 'Recent orders' },
      children: ['ordersHeader', 'ordersTable'],
    },
    ordersHeader: {
      type: 'Box',
      props: { direction: 'row', justify: 'between', align: 'center' },
      children: ['ordersHeaderText', 'reorderBtn'],
    },
    ordersHeaderText: {
      type: 'Text',
      props: {
        value: 'Reorder a recent shipment in one click.',
        variant: 'muted',
      },
      children: [],
    },
    reorderBtn: {
      type: 'Button',
      props: { label: 'Reorder PIL-2043', size: 'sm', variant: 'secondary' },
      on: { press: { action: 'reorder', params: { order: 'PIL-2043' } } },
      children: [],
    },
    ordersTable: {
      type: 'Table',
      props: {
        columns: [
          { key: 'order', label: 'Order' },
          { key: 'date', label: 'Date' },
          { key: 'items', label: 'Items', align: 'right' },
          { key: 'status', label: 'Status' },
          { key: 'total', label: 'Total', align: 'right' },
        ],
        rows: { $state: '/orders' },
      },
      children: [],
    },

    activityCard: {
      type: 'Card',
      props: { title: 'Recent activity' },
      children: ['activityTable'],
    },
    activityTable: {
      type: 'Table',
      props: {
        columns: [
          { key: 'when', label: 'When' },
          { key: 'event', label: 'Activity' },
        ],
        rows: { $state: '/activity' },
      },
      children: [],
    },

    ticketsCard: {
      type: 'Card',
      props: { title: 'Support tickets' },
      children: ['ticketsTable'],
    },
    ticketsTable: {
      type: 'Table',
      props: {
        columns: [
          { key: 'id', label: 'Ticket' },
          { key: 'subject', label: 'Subject' },
          { key: 'priority', label: 'Priority' },
          { key: 'status', label: 'Status' },
          { key: 'updated', label: 'Updated' },
        ],
        rows: { $state: '/tickets' },
      },
      children: [],
    },
  },
};

/** Initial state for {@link cannedPortalSpec}. */
export const cannedPortalState: Record<string, unknown> = {
  // KPI values that actions recompute live.
  kpis: {
    activeOrders: '12',
    openInvoicesTotal: '$19,765.00',
    openInvoicesBadge: '1 overdue',
    openInvoicesBadgeVariant: 'warning',
  },
  // Indexes 0–2 are wired to the interactive invoice rows above; `amountValue`
  // feeds the open-invoices KPI recompute.
  invoices: [
    {
      invoice: 'INV-8841',
      amount: '$4,200.00',
      amountValue: 4200,
      status: 'Open',
      statusVariant: 'secondary',
      paid: false,
      resent: false,
      resendLabel: 'Resend',
      dueLabel: 'Due Jul 5, 2026 · Net 30',
      po: 'PIL-2041',
      issueDate: 'Jun 5, 2026',
      terms: 'Net 30',
      paymentMethod: 'ACH •• 6042',
      billTo: 'Pillar Foods, Inc.',
      billToAddress: '210 Harbor St, Oakland, CA 94607',
      subtotal: '$4,200.00',
      tax: '$0.00',
      total: '$4,200.00',
      overdue: false,
      overdueNote: '',
      lineItems: [
        { item: 'Weekly produce — sage greens', qty: 4, amount: '$3,200.00' },
        { item: 'Cold-chain handling', qty: 1, amount: '$1,000.00' },
      ],
    },
    {
      invoice: 'INV-8840',
      amount: '$12,540.00',
      amountValue: 12540,
      status: 'Open',
      statusVariant: 'secondary',
      paid: false,
      resent: false,
      resendLabel: 'Resend',
      dueLabel: 'Due Jul 1, 2026 · Net 30',
      po: 'PIL-2043',
      issueDate: 'Jun 1, 2026',
      terms: 'Net 30',
      paymentMethod: 'ACH •• 6042',
      billTo: 'Pillar Foods, Inc.',
      billToAddress: '210 Harbor St, Oakland, CA 94607',
      subtotal: '$12,540.00',
      tax: '$0.00',
      total: '$12,540.00',
      overdue: false,
      overdueNote: '',
      lineItems: [
        { item: 'Packaging — recycled cartons', qty: 12, amount: '$8,400.00' },
        { item: 'Logistics — regional freight', qty: 1, amount: '$4,140.00' },
      ],
    },
    {
      invoice: 'INV-8838',
      amount: '$3,025.00',
      amountValue: 3025,
      status: 'Overdue',
      statusVariant: 'warning',
      paid: false,
      resent: false,
      resendLabel: 'Resend',
      dueLabel: 'Overdue — was due Jun 20, 2026',
      po: 'PIL-2046',
      issueDate: 'May 21, 2026',
      terms: 'Net 30',
      paymentMethod: 'ACH •• 6042',
      billTo: 'Pillar Foods, Inc.',
      billToAddress: '210 Harbor St, Oakland, CA 94607',
      subtotal: '$3,025.00',
      tax: '$0.00',
      total: '$3,025.00',
      overdue: true,
      overdueNote: 'This invoice is past due — pay now to avoid a late fee.',
      lineItems: [
        { item: 'Equipment rental — chiller', qty: 1, amount: '$2,400.00' },
        { item: 'Services — install + calibration', qty: 1, amount: '$625.00' },
      ],
    },
  ],
  // Backing state for the invoice detail slide-in (filled by `openInvoice`).
  invoiceSheet: {
    open: false,
    title: 'Invoice',
    amount: '',
    status: '',
    statusVariant: 'secondary',
    dueLabel: '',
    po: '',
    issueDate: '',
    terms: '',
    paymentMethod: '',
    billTo: '',
    billToAddress: '',
    subtotal: '',
    tax: '',
    total: '',
    overdue: false,
    overdueNote: '',
    lineItems: [],
  },
  orders: [
    {
      order: 'PIL-2041',
      date: 'Jun 18, 2026',
      items: 14,
      status: 'Shipped',
      total: '$4,200.00',
    },
    {
      order: 'PIL-2042',
      date: 'Jun 14, 2026',
      items: 6,
      status: 'Processing',
      total: '$1,890.00',
    },
    {
      order: 'PIL-2043',
      date: 'Jun 09, 2026',
      items: 28,
      status: 'Delivered',
      total: '$12,540.00',
    },
    {
      order: 'PIL-2044',
      date: 'Jun 02, 2026',
      items: 3,
      status: 'Delivered',
      total: '$640.00',
    },
    {
      order: 'PIL-2045',
      date: 'May 27, 2026',
      items: 19,
      status: 'Delivered',
      total: '$7,310.00',
    },
    {
      order: 'PIL-2046',
      date: 'May 21, 2026',
      items: 11,
      status: 'Delivered',
      total: '$3,025.00',
    },
  ],
  activity: [
    { when: 'Jun 21, 2026', event: 'Signed in from Chicago, IL' },
    { when: 'Jun 18, 2026', event: 'Order PIL-2041 shipped' },
  ],
  tickets: [
    {
      id: 'TKT-512',
      subject: 'Adjust standing weekly produce order',
      priority: 'Normal',
      status: 'Open',
      updated: 'Jun 21, 2026',
    },
    {
      id: 'TKT-509',
      subject: 'Invoice INV-8838 payment terms',
      priority: 'High',
      status: 'Waiting on you',
      updated: 'Jun 19, 2026',
    },
    {
      id: 'TKT-503',
      subject: 'Add cold-chain handling to PIL-2047',
      priority: 'Normal',
      status: 'Resolved',
      updated: 'Jun 12, 2026',
    },
    {
      id: 'TKT-498',
      subject: 'Update billing contact',
      priority: 'Low',
      status: 'Resolved',
      updated: 'Jun 04, 2026',
    },
  ],
};
